import { LANGUAGE_TYPES } from "@src/constants";
import i18next from "i18next";

const translateDutch = i18next.getFixedT("nl");
const translateEnglish = i18next.getFixedT("en");
const translateGerman = i18next.getFixedT("de");
const translateFrench = i18next.getFixedT("fr");
const translateSpanish = i18next.getFixedT("es");

/**
 * Returns a forced translation in a specific language that can be different from the application language
 * @param lokaliseKey - Lokalise key string
 * @param languageCode - Language code of the session
 * @return Translated lokalise key value
 */
export const getTranslatedKey = (
  lokaliseKey: string,
  languageCode: string | null | undefined,
): string => {
  const translations: Record<string, (key: string) => string> = {
    [LANGUAGE_TYPES.DUTCH]: translateDutch,
    [LANGUAGE_TYPES.GERMAN]: translateGerman,
    [LANGUAGE_TYPES.FRENCH]: translateFrench,
    [LANGUAGE_TYPES.SPANISH]: translateSpanish,
  };

  // Select the appropriate translation function or default to translateEnglish.
  const translate =
    languageCode &&
    typeof languageCode === "string" &&
    translations[languageCode]
      ? translations[languageCode]
      : translateEnglish;

  return translate(lokaliseKey) || translateEnglish(lokaliseKey);
};

/**
 * Returns translated language name based on 4-letter [languageCode]
 * @param t - Translation function provided by useTranslation()
 * @param languageCode - 4-letter language code
 * @return Translated language name
 */
export const getLanguageNameBasedOnAbbreviation = (
  t: ((key: string) => string) | null,
  languageCode: string,
): string => {
  if (!t) {
    return `SessionLanguage.${languageCode}`;
  }
  if (
    Object.values(LANGUAGE_TYPES).includes(
      languageCode as keyof typeof LANGUAGE_TYPES,
    )
  ) {
    return t(`SessionLanguage.${languageCode}`);
  }
  return t("SessionLanguage.en-GB");
};

/**
 * Returns translated theme name based on theme key
 * @param t - Translation function provided by useTranslation()
 * @param themeKey - Theme key
 * @return Translated theme name
 */
export const getTranslatedThemeName = (
  t: ((key: string) => string) | null,
  themeKey: string,
): string => {
  if (!t || !themeKey) {
    return "";
  }
  return t(`Themes.${themeKey}.Name`);
};

/**
 * Returns translated topic name based on topic key in session language
 * @param topicKey - Topic key
 * @param languageCode - 4-letter language code
 * @return Translated topic name
 */
export const getTranslatedTopicName = (
  topicKey: string | null | undefined,
  languageCode: string | null | undefined,
): string | null => {
  if (!topicKey) return null;
  return getTranslatedKey(`Topics.${topicKey}.Name`, languageCode);
};

/**
 * Returns translated theme name based on theme key in session language
 * @param themeKey - Theme key
 * @param languageCode - 4-letter language code
 * @return Translated theme name
 */
export const getTranslatedThemeNameInSessionLanguage = (
  themeKey: string | null | undefined,
  languageCode: string | null | undefined,
): string | null => {
  return themeKey
    ? getTranslatedKey(`Themes.${themeKey}.Name`, languageCode)
    : null;
};
